import { useEffect, useMemo, useId, useRef } from 'react';
import {
  Button,
  buttonVariants,
  cn,
  documentPreviewPath,
  HelpTip,
  DocumentPreviewView,
  PdfViewer,
  StatementBasisLine,
  categoryPathLabel,
  usesDocumentPreview,
  useEscapeToClose,
  useDialogFocus,
} from '@dataroom/shared';
import type { RoomDocument } from '@dataroom/shared';
import { useDocumentContent } from '@/lib/useDocumentContent';
import { usePageReadTracker } from '@/lib/page-reads';
import { buildWatermarkText, watermarkTileDataUrl } from '@/lib/viewer';

interface DocumentViewerProps {
  roomId: string;
  document: RoomDocument;
  /** Identity stamped into the watermark (buyer email when known — see
   * getViewerIdentity — otherwise room context). */
  viewerIdentity: string;
  /**
   * Override the content endpoint this viewer fetches from. Defaults to the
   * general permission-scoped document content path
   * (`/api/v1/rooms/:roomId/documents/:documentId/content`). The buyer's LOI
   * card passes the LOI-SCOPED read
   * (`/api/v1/rooms/:roomId/loi/:loiId/document/content`) so their own signed LOI
   * opens without depending on the tag choke point. Same response
   * contract (inline bytes, `no-store`), so nothing else in the viewer changes.
   */
  contentPath?: string;
  /**
   * Show a Download button — ONLY when the server says this caller may download
   * this document: the document listing's `canDownload`, computed with the
   * content route's own `canDownloadDocument`. When set, the button requests
   * `?download=1`, so the server still makes (and audits) the decision.
   */
  canDownload?: boolean;
  onClose: () => void;
}

/**
 * Protected, VIEW-ONLY document viewer for the buyer room (issue #14).
 *
 * Fetches the document bytes from the same-origin content endpoint
 * (GET /api/v1/rooms/:roomId/documents/:documentId/content — served inline with
 * `Cache-Control: no-store`), renders them inline by Content-Type, and overlays
 * a tiled viewer-identity watermark. Office files (spreadsheets, Word, CSV, …),
 * which the content endpoint only serves as an attachment, are shown from the
 * server-built read-only preview instead (ADR-0033): a sheet grid, Word HTML or
 * text, under the same watermark and deterrents. PDFs are drawn by pdf.js onto canvases in
 * our own viewer (ADR-0027) — never the browser's PDF plugin, whose toolbar
 * would hand a view-only buyer one-click Download and Print. There is NO
 * download affordance unless `canDownload` says the server permits it.
 *
 * SCOPE OF PROTECTION — this is PRESENTATION-LAYER, DETERRENT-GRADE protection,
 * NOT security and NOT DRM. The watermark, the print suppression, the
 * context-menu block and the text-selection block all raise the effort/traceability
 * of casual exfiltration (screenshots, right-click-save, print-to-PDF); a
 * determined viewer with devtools or a camera can still capture what is on their
 * own screen. TRUE protection — server-side/burned-in watermarking and real DRM
 * — is a tracked follow-up that needs a document-rendering dependency decision.
 *
 * The bytes are BINARY, so this fetches with the raw `fetch` API (credentialed)
 * and reads a Blob → object URL, rather than going through the JSON api client.
 *
 * PAGE-LEVEL READING (ADR-0056): for a room document (not the buyer's own LOI,
 * which has its own content path) the viewer reports which page is in view and
 * for how long — PDFs and rendered pages page by page, anything else as one
 * page — batched every 15 s and on close (lib/page-reads.ts). The server records
 * it only for a buyer and only for a document they can see.
 */
export function DocumentViewer({
  roomId,
  document,
  viewerIdentity,
  contentPath,
  canDownload = false,
  onClose,
}: DocumentViewerProps) {
  // The watermark label (identity + open time) is stable for the lifetime of
  // this open; recompute only when the identity changes.
  const watermark = useMemo(() => buildWatermarkText(viewerIdentity), [viewerIdentity]);
  const watermarkImage = useMemo(() => watermarkTileDataUrl(watermark), [watermark]);
  // Tiled diagonal identity watermark over whatever is on screen — never
  // intercepting pointer events, semi-transparent, hidden in print (the content
  // is hidden there too).
  const watermarkLayer = (
    <div
      aria-hidden="true"
      data-watermark=""
      className="pointer-events-none absolute inset-0 print:hidden"
      style={{ backgroundImage: watermarkImage, backgroundRepeat: 'repeat' }}
    />
  );

  const contentUrl = contentPath ?? `/api/v1/rooms/${roomId}/documents/${document.id}/content`;

  // Office files (and TIFF / HEIC images) come from the read-only preview
  // endpoint — same visibility check as the bytes. An overridden content path
  // (the buyer's own LOI) has no preview route, so it keeps the bytes-only viewer.
  const previewPath = contentPath ? null : documentPreviewPath(roomId, document.id);
  const previewUrl = previewPath && usesDocumentPreview(document.name) ? previewPath : null;

  const { content, error, loading, versionId } = useDocumentContent(
    contentUrl,
    undefined,
    previewUrl,
    previewPath,
  );

  // Page-level reading for room documents only (never an overridden content
  // path), and only once the server has named the version it served — the
  // reports carry it, and the server records nothing for any other version.
  const tracker = usePageReadTracker(
    roomId,
    document.id,
    contentPath === undefined ? (versionId ?? null) : null,
  );
  // A document with no pages of its own (an image, text, a sheet or Word preview)
  // is one page; PDFs and rendered pages report their own page as it changes.
  // Nothing on screen (no preview for the type) is nothing read.
  const singlePage =
    content?.kind === 'image' ||
    content?.kind === 'text' ||
    (content?.kind === 'office' &&
      content.preview.kind !== 'pages' &&
      content.preview.kind !== 'unsupported');
  useEffect(() => {
    if (singlePage) tracker?.setPage(1, 1);
  }, [singlePage, tracker]);
  const onPageChange = (page: number, numPages: number) => tracker?.setPage(page, numPages);

  // Close on Escape for keyboard users — only while this is the topmost dialog
  // (it opens over another dialog, which must stay open on the same key press).
  useEscapeToClose(onClose);
  // Focus moves into the viewer, Tab stays inside, and closing hands focus
  // back to whatever opened it (the document name or the ⋯ menu).
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogFocus(dialogRef);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex flex-col bg-scrim p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose}
    >
      <div
        className="mx-auto flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-lg border bg-background shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1">
              <h2 id={titleId} className="truncate text-sm font-semibold">
                <span className="sr-only">Viewing </span>
                {document.name}
              </h2>
              {/* The buyer's own signed LOI is shown as uploaded, not redacted. */}
              {contentPath === undefined ? (
                <HelpTip id="buyer.viewer.redaction" className="shrink-0" />
              ) : null}
            </div>
            {/* Guarded: a malformed row (no categoryPath) must not white-screen the room. */}
            {Array.isArray(document.categoryPath) && document.categoryPath.length > 0 ? (
              <p className="truncate text-xs text-muted-foreground">
                {categoryPathLabel(document.categoryPath)}
              </p>
            ) : null}
            {/* A financial statement's accounting basis (ADR-0053) — a fact
                about this document, shown only because the buyer can see it. */}
            <StatementBasisLine
              value={document.statementBasis}
              className="truncate text-xs text-muted-foreground"
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canDownload ? (
              // The server makes (and audits) the decision: `?download=1` is
              // served as an attachment only under a download-allowed grant.
              <a
                href={`${contentUrl}?download=1`}
                download={document.name}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
              >
                Download
              </a>
            ) : (
              // The "View only" ethos stays visible in the viewer chrome; there
              // is intentionally no download/print/save control.
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                View only
              </span>
            )}
            <HelpTip id="buyer.viewer.view-only" />
            <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close viewer">
              Close
            </Button>
          </div>
        </div>

        {/*
          The protected viewing surface. DETERRENTS (deterrent-grade, not security):
          - PDFs are canvas-only (pdf.js, no text layer, no browser PDF plugin), so
            there is no plugin toolbar, no Download/Print button, no text to select.
          - `select-none` blocks casual text selection / copy elsewhere.
          - `onContextMenu`/`onDragStart` preventDefault blocks right-click
            "Save image as…" and drag-out over images and PDF page canvases.
          The document layer carries `print:hidden`; a print notice replaces it in
          the `@media print` context (Tailwind's `print:` variant compiles to
          `@media print`) so a browser print / print-to-PDF captures the notice,
          not the document. All of this is bypassable by a determined viewer (a
          screenshot can never be prevented) — see the component docblock.
          The surface itself never scrolls (the inner layer does), so the
          watermark layer always covers what is on screen.
        */}
        <div
          className="relative min-h-0 flex-1 select-none bg-muted/30"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {content?.kind === 'pdf' ? (
            <PdfViewer
              data={content.data}
              title={document.name}
              protect
              overlay={watermarkLayer}
              className="h-full print:hidden"
              onPageChange={onPageChange}
            />
          ) : content?.kind === 'office' && content.preview.kind !== 'unsupported' ? (
            <>
              <DocumentPreviewView
                preview={content.preview}
                title={document.name}
                className="h-full print:hidden"
                onPageChange={onPageChange}
              />
              {watermarkLayer}
            </>
          ) : (
            <>
              <div className="h-full overflow-auto print:hidden">
                <div className="flex min-h-full items-center justify-center p-4">
                  {loading ? (
                    <p className="text-sm text-muted-foreground">Loading document…</p>
                  ) : error ? (
                    <p className="max-w-md text-center text-sm text-destructive">{error}</p>
                  ) : content?.kind === 'image' ? (
                    <img
                      src={content.url}
                      alt={document.name}
                      draggable={false}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : content?.kind === 'text' ? (
                    <pre className="w-full self-stretch whitespace-pre-wrap break-words rounded border bg-background p-4 text-left text-xs leading-relaxed">
                      {content.text}
                    </pre>
                  ) : content?.kind === 'office' &&
                    content.preview.kind === 'unsupported' &&
                    content.preview.reason === 'unreadable' ? (
                    <p className="max-w-md text-center text-sm text-muted-foreground">
                      {canDownload
                        ? 'This file couldn’t be read for a preview — use Download to open it.'
                        : 'View-only — this file couldn’t be read for a preview.'}
                    </p>
                  ) : content ? (
                    <p className="max-w-md text-center text-sm text-muted-foreground">
                      {canDownload
                        ? 'A preview isn’t available for this file type — use Download to open it.'
                        : 'View-only — a preview isn’t available for this file type.'}
                    </p>
                  ) : null}
                </div>
              </div>
              {watermarkLayer}
            </>
          )}

          {/* Shown ONLY in the @media print context (see deterrents note above). */}
          <div className="hidden min-h-full flex-col items-center justify-center p-8 text-center print:flex">
            <p className="text-base font-semibold">Printing is disabled</p>
            <p className="mt-1 text-sm">
              This document is view-only. {watermark}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
